import React, { useEffect, useState } from 'react';
import { InputAdornment, TextField } from '@mui/material';

/**
 * Decimal places each currency is billed in. Anything not listed uses 2.
 *
 * Mirrors the same list on the server, which converts the amount to the
 * integer "minor units" Stripe charges in. Keeping the input in step means the
 * admin cannot type a precision that will be silently rounded away — 500 JPY has
 * no cents, 1.234 KWD does.
 */
const CURRENCY_DECIMALS: Record<string, number> = {
  bif: 0, clp: 0, djf: 0, gnf: 0, jpy: 0, kmf: 0, krw: 0, mga: 0,
  pyg: 0, rwf: 0, ugx: 0, vnd: 0, vuv: 0, xaf: 0, xof: 0, xpf: 0,
  bhd: 3, jod: 3, kwd: 3, omr: 3, tnd: 3,
};

const decimalsFor = (currency: string) => CURRENCY_DECIMALS[(currency || 'usd').toLowerCase()] ?? 2;

/** How the amount reads when the field is not being typed into: "49.00". */
const canonical = (value: unknown, decimals: number) => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed.toFixed(decimals) : (0).toFixed(decimals);
};

const patternFor = (decimals: number) =>
  decimals === 0 ? /^\d*$/ : new RegExp(`^\\d*(\\.\\d{0,${decimals}})?$`);

interface PriceFieldProps {
  label: string;
  value: number | string | null | undefined;
  onChange: (value: number) => void;
  /** Used for the prefix and to decide how many decimals are allowed. */
  currency: string;
  helperText?: string;
  disabled?: boolean;
}

/**
 * Money input.
 *
 * Replaces `<TextField type="number" value={form.price ?? 0} />`, which had
 * three problems, all of them worse now that the amount is pushed straight to
 * Stripe:
 *
 *  1. **Decimals were impossible to type.** Every keystroke ran through
 *     `Number()`, so the moment you typed "49." it became 49 and the field
 *     re-rendered as "49" — the dot was swallowed and you could never reach
 *     "49.99".
 *  2. **The field could not be cleared.** An empty string parses to 0, which
 *     rendered as "0", so you had to type around a leading zero.
 *  3. **A number input responds to the scroll wheel**, so scrolling the dialog
 *     with the cursor over the price silently changed it.
 *
 * The fix is to keep the half-typed text as its own state while the field has
 * focus, and only mirror the parsed value back into the display on blur. That
 * lets intermediate states like "49." and "" exist without the parsed number
 * fighting them.
 */
const PriceField = ({ label, value, onChange, currency, helperText, disabled }: PriceFieldProps) => {
  const decimals = decimalsFor(currency);
  const [focused, setFocused] = useState(false);
  const [draft, setDraft] = useState(() => canonical(value, decimals));

  // While focused the draft is the source of truth, so the parsed value must not
  // be allowed to rewrite what is being typed. On blur, and on any change that
  // came from elsewhere (opening the dialog on another row, switching currency),
  // the display snaps back to the canonical form.
  useEffect(() => {
    if (focused) return;

    const text = canonical(value, decimals);
    setDraft(text);

    // Switching to a currency with fewer decimals has to correct the stored
    // value too, not just the display: 49.99 shown as "50" for JPY while the
    // form still held 49.99 would save a figure the admin never saw, and Stripe
    // would be charged the rounded one — leaving our record and the real charge
    // disagreeing. Re-emitting converges after one render, since the rounded
    // value formats back to itself.
    const rounded = Number(text);
    if (Number(value ?? 0) !== rounded) onChange(rounded);
    // onChange is an inline arrow in the parent and would retrigger every
    // render; the emit above is guarded so leaving it out cannot stall.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, decimals, focused]);

  const handleChange = (raw: string) => {
    // Accept a comma as the decimal separator — European keyboards produce one,
    // and rejecting the keystroke outright looks like a broken field.
    const normalized = raw.replace(',', '.');

    // Reject rather than sanitise: silently dropping characters makes it look
    // like keys are being missed. Refusing the keystroke shows the limit.
    if (normalized !== '' && !patternFor(decimals).test(normalized)) return;

    setDraft(normalized);
    onChange(normalized === '' || normalized === '.' ? 0 : Number(normalized));
  };

  return (
    <TextField
      label={label}
      // Deliberately text, not number: no spinner arrows, no scroll-wheel
      // changes, and full control over what a keystroke is allowed to do.
      type="text"
      inputMode="decimal"
      value={draft}
      disabled={disabled}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      onChange={(e) => handleChange(e.target.value)}
      helperText={helperText}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">{(currency || 'USD').toUpperCase()}</InputAdornment>
        ),
      }}
      inputProps={{ style: { textAlign: 'right' } }}
    />
  );
};

export default PriceField;
